import {
  REQUEST_MANUAL_ORGANIZATION_BACKUP,
  UPDATE_ORGANIZATION,
  UPDATE_ORGANIZATION_PHOTO,
  ORGANIZATION_FETCH_ERROR,
  ORGANIZATION_JOINED_FETCH_START,
  ORGANIZATION_JOINED_FETCH_ERROR,
  ORGANIZATION_JOINED_FETCHED,
  ORGANIZATION_GENERATE_INVITE_TOKEN,
  ORGANIZATION_REMOVE_INVITE_TOKEN,
  UPDATE_ORGANIZATION_AUTH_PROVIDER,
} from './actionTypes'

import { createFromRaw as createPublicInfoFromRaw } from './recordPublicInfo'
import * as ConfigSelectors from 'data/config/selectors'
import * as OrganizationSelectors from 'data/organizations/selectors'
import * as AuthSelectors from 'data/auth/selectors'
import OrganizationsAPI from 'remote/organizations'
import { removeOrganizationFromJoined } from 'helpers/OrganizationsHelpers'
import { dataFetched } from 'data/graphql/reducer'
import {
  createSuccessToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'
import { getOtherOrganizationAppURL } from 'helpers/URLHelper'
import { clearTokens } from '../tokenStore'

export function fetchOrganizationError(error) {
  return {
    type: ORGANIZATION_FETCH_ERROR,
    payload: error,
  }
}

export const fetchOrganization = domain => ({ getState, dispatch }) => {
  const apiEndpoint = ConfigSelectors.getApiEndpoint(getState())
  return OrganizationsAPI.fetchOrganizationFromDomain(
    { apiEndpoint },
    { domain }
  ).then(
    organization => {
      if (!organization) {
        dispatch(
          fetchOrganizationError(`The '${domain}' does not match any team`)
        )
        return
      }
      dispatch(dataFetched(organization))
      return organization
    },
    error => dispatch(fetchOrganizationError(error))
  )
}

export function fetchJoinedOrganizationStart(organizationId) {
  return {
    type: ORGANIZATION_JOINED_FETCH_START,
    payload: { organizationId },
  }
}

export function fetchOrganizationJoinedSuccess(organizationPublicRaw) {
  return {
    type: ORGANIZATION_JOINED_FETCHED,
    payload: createPublicInfoFromRaw(organizationPublicRaw),
  }
}

export function fetchOrganizationJoinedError(
  { getState },
  { organizationId, error }
) {
  if (error.status > 400) {
    const cookieDomain = ConfigSelectors.getCookieDomain(getState())
    removeOrganizationFromJoined(cookieDomain, organizationId)
  }
  return {
    type: ORGANIZATION_JOINED_FETCH_ERROR,
    payload: { organizationId, error },
  }
}

export const fetchJoinedOrganization = organizationId => ({
  getState,
  dispatch,
}) => {
  const apiEndpoint = ConfigSelectors.getApiEndpoint(getState())
  OrganizationsAPI.fetchOrganizationPublicInfo(
    { apiEndpoint },
    organizationId
  ).then(
    organization => dispatch(fetchOrganizationJoinedSuccess(organization)),
    error =>
      dispatch(
        fetchOrganizationJoinedError({ getState }, { organizationId, error })
      )
  )
  return fetchJoinedOrganizationStart(organizationId)
}

export const fetchJoinedOrganizations = () => {
  return ({ getState, dispatch, graphQLClient }) => {
    const currentOrganizationId = OrganizationSelectors.getOrganizationId(
      getState()
    )
    return {
      type: 'FETCH_JOINED_ORGANIZATIONS',
      payload: graphQLClient
        .query({
          query: OrganizationsAPI.showJoinedOrganizations,
        })
        .then(({ data }) => {
          data.showJoinedOrganizations.forEach(organization => {
            if (organization.id !== currentOrganizationId) {
              dispatch(fetchOrganizationJoinedSuccess(organization))
            }
          })
        }),
    }
  }
}

export const updateOrganization = organizationData => {
  return ({ getState, dispatch, graphQLClient }) => {
    return {
      type: UPDATE_ORGANIZATION,
      payload: graphQLClient
        .mutate({
          query: OrganizationsAPI.updateOrganization,
          variables: {
            organization: organizationData,
          },
        })
        .then(
          ({ data }) => {
            dispatch(dataFetched(data.updateCurrentOrganization))
            dispatch(
              createSuccessToasterMessage({
                title: 'Your modifications have been saved !',
              })
            )
          },
          ({ errors }) => {
            if (errors.length) {
              dispatch(
                createErrorToasterMessage({
                  title: errors[0].message,
                })
              )
            }
          }
        ),
    }
  }
}

export const updateOrganizationDomain = domain => {
  return ({ dispatch, graphQLClient }) => {
    return {
      type: UPDATE_ORGANIZATION,
      payload: graphQLClient
        .mutate({
          query: OrganizationsAPI.updateOrganizationDomain,
          variables: {
            domain,
          },
        })
        .then(
          () => {
            clearTokens()
            window.location = getOtherOrganizationAppURL(domain)
          },
          ({ errors }) => {
            if (errors.length) {
              dispatch(
                createErrorToasterMessage({
                  title: errors[0].message,
                })
              )
            }
          }
        ),
    }
  }
}

export const setOrganizationAuthProvider = authProviderData => {
  return ({ dispatch, graphQLClient, getState }) => {
    const apiEndpoint = ConfigSelectors.getApiEndpoint(getState())
    const apiToken = AuthSelectors.getAuthTokens(getState()).apiToken
    return {
      type: UPDATE_ORGANIZATION_AUTH_PROVIDER,
      payload: graphQLClient
        .mutate({
          query: OrganizationsAPI.setOrganizationAuthProvider,
          variables: {
            ...authProviderData,
          },
        })
        .then(
          () => {
            window.location = `${apiEndpoint}/auth/openId/setup?apiToken=${apiToken}`
          },
          ({ errors }) => {
            if (errors.length) {
              dispatch(
                createErrorToasterMessage({
                  title: 'We failed to enable SSO, please contact support',
                })
              )
            }
          }
        ),
    }
  }
}

export const generateInviteToken = () => ({
  getState,
  dispatch,
  graphQLClient,
}) => ({
  type: ORGANIZATION_GENERATE_INVITE_TOKEN,
  payload: {
    promise: graphQLClient
      .mutate({
        query: OrganizationsAPI.generateInviteToken,
      })
      .then(
        ({ data }) => {
          const state = getState()
          const organizationId = OrganizationSelectors.getOrganizationId(state)
          dispatch(
            dataFetched({
              id: organizationId,
              inviteToken: data.generateInviteToken,
              __typename: 'Organization',
            })
          )
          dispatch(
            createSuccessToasterMessage({
              title: 'Your invite link is ready!',
            })
          )
        },
        ({ errors }) => {
          if (errors.length) {
            dispatch(
              createErrorToasterMessage({
                title: errors[0].message,
              })
            )
          }
        }
      ),
  },
})

export const removeInviteToken = () => ({ dispatch, graphQLClient }) => ({
  type: ORGANIZATION_REMOVE_INVITE_TOKEN,
  payload: {
    promise: graphQLClient
      .mutate({
        query: OrganizationsAPI.removeInviteToken,
      })
      .then(
        response => {
          dispatch(dataFetched(response.data.removeInviteToken))
          dispatch(
            createSuccessToasterMessage({
              title: 'The invite link has been disabled.',
            })
          )
        },
        ({ errors }) => {
          if (errors.length) {
            dispatch(
              createErrorToasterMessage({
                title: errors[0].message,
              })
            )
          }
        }
      ),
  },
})

export const updateOrganizationPhoto = ({ photoFile }) => ({
  getState,
  dispatch,
}) => {
  const state = getState()
  const { apiEndpoint } = ConfigSelectors.getConfig(state)
  const apiToken = AuthSelectors.getAuthTokens(getState()).apiToken
  const organizationId = OrganizationSelectors.getOrganizationId(state)
  const promise = OrganizationsAPI.uploadOrganizationPhotoURL(
    { apiEndpoint, apiToken },
    {
      organizationId,
      photoFile,
    }
  ).then(photoURL => {
    dispatch(updateOrganization({ photoURL }))
  })
  return {
    type: UPDATE_ORGANIZATION_PHOTO,
    payload: promise,
  }
}

export const requestManualOrganizationBackup = () => {
  return ({ getState, dispatch, graphQLClient }) => {
    return {
      type: REQUEST_MANUAL_ORGANIZATION_BACKUP,
      meta: {
        analytics: {
          type: REQUEST_MANUAL_ORGANIZATION_BACKUP,
        },
      },
      payload: graphQLClient
        .mutate({
          query: OrganizationsAPI.requestManualOrganizationBackup,
        })
        .then(
          ({ data }) => {
            dispatch(dataFetched(data.requestManualOrganizationBackup))
            dispatch(
              createSuccessToasterMessage({
                title: 'Backup in progress',
                detail: `Backed up content will be emailed to team owners`,
              })
            )
          },
          ({ errors }) => {
            if (errors.length) {
              dispatch(
                createErrorToasterMessage({
                  title: errors[0].message,
                })
              )
            }
          }
        ),
    }
  }
}
